// Laat dit scherm op dit moment echt iets zien?
//
// De Pi blijft 's avonds aan, alleen de tv gaat uit. Voor de browser
// verandert er dan niets: hij blijft ophalen, blijft rekenen, en speelt
// animaties af tegen een zwart scherm. 's Ochtends denkt het dashboard dus
// dat het de hele nacht heeft staan kijken, en dan komt de kaart "Verkopen
// gemist" nooit - precies het geval waar die kaart voor bedoeld is.
//
// Wat wel verandert is het tekenen zelf. Valt het beeld weg, dan levert de
// browser geen frames meer af, of nog maar een enkele per seconde. Dat is
// hier het signaal: we tellen de frames, en zakt dat lang genoeg onder de
// vloer, dan staat deze tv uit.
//
// Bewust twee losse klokken: requestAnimationFrame telt de frames, en een
// gewone setInterval kijkt hoeveel het er waren. Die tweede loopt door als
// het tekenen stilvalt, en juist dat verschil is wat we willen weten.
//
// De vloer ligt laag (screenMinFps) en het moet lang genoeg duren
// (darkAfterMs, per scherm in te stellen), want een Pi die het even zwaar
// heeft is geen tv die uitgaat.
//
// Hoe lang het donker was is ook wat het inhalen aanstuurt: useSaleEvents
// telt die tijd op en legt hem naast catchUpDarkMs. Dat kan alleen omdat we
// het hier echt meten - het gat tussen twee ophaalrondes zegt er niets over.

import { screenCheckMs, screenMinFps } from "../constants";
import { darkAfterMs, deviceName } from "./device";

const listeners = new Set();

let watchers = 0;
let frames = 0;
let lastFrameAt = 0;
let lastCheckAt = 0;
let lowSince = 0;
let awake = true;
let darkAt = null;
let rafId = 0;
let timerId = 0;

function tick() {
  frames += 1;
  lastFrameAt = Date.now();
  rafId = requestAnimationFrame(tick);
}

function announce(event) {
  listeners.forEach((listener) => listener(event));
}

function clock(at) {
  return new Date(at).toTimeString().slice(0, 5);
}

function goDark(at) {
  awake = false;
  darkAt = at;
  console.info(`[dashboard] ${deviceName()}: beeld weg om ${clock(at)}`);
  announce({ awake: false, darkAt: at, sleptMs: 0 });
}

function goAwake(at) {
  const was = darkAt;
  const sleptMs = was ? at - was : 0;
  awake = true;
  darkAt = null;
  const minutes = Math.round(sleptMs / 60000);
  console.info(
    `[dashboard] ${deviceName()}: beeld terug om ${clock(at)}, ${minutes} min donker`,
  );
  announce({ awake: true, darkAt: was, sleptMs });
}

// Eén meetronde: hoeveel frames zijn er in dit venster langsgekomen?
function check() {
  const at = Date.now();
  const span = at - lastCheckAt;
  lastCheckAt = at;

  const fps = span > 0 ? (frames * 1000) / span : 0;
  frames = 0;

  // Een verborgen tabblad is geen twijfelgeval: dan tekent hij zeker niet.
  const hidden =
    typeof document !== "undefined" && document.visibilityState === "hidden";
  const low = hidden || fps < screenMinFps;

  if (!low) {
    lowSince = 0;
    if (!awake) goAwake(at);
    return;
  }

  // Het moment waarop het beeld wegviel is het laatste frame dat we nog
  // zagen, niet het moment waarop we het merken: anders zou de kaart
  // straks twintig seconden te laat zeggen hoe laat de tv uitging.
  if (!lowSince) lowSince = lastFrameAt || at;
  if (awake && at - lowSince >= darkAfterMs()) goDark(lowSince);
}

// Staat dit scherm aan? Zonder meting (server, test) zeggen we ja: dan doet
// het dashboard gewoon wat het altijd deed.
export function isScreenAwake() {
  return awake;
}

// Sinds wanneer het beeld weg is, of null als het aan staat.
export function screenDarkSince() {
  return darkAt;
}

// Meeluisteren met aan- en uitgaan. Geeft een opzegfunctie terug.
export function onScreenChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Beginnen met meten. Meerdere keren aanroepen mag: er loopt er maar één.
export function startScreenWatch() {
  if (typeof window === "undefined") return () => {};

  watchers += 1;
  if (watchers === 1) {
    lastCheckAt = Date.now();
    lastFrameAt = lastCheckAt;
    frames = 0;
    lowSince = 0;
    rafId = requestAnimationFrame(tick);
    timerId = setInterval(check, screenCheckMs);
  }

  return () => {
    watchers = Math.max(0, watchers - 1);
    if (watchers > 0) return;
    cancelAnimationFrame(rafId);
    clearInterval(timerId);
    // Wie straks opnieuw begint hoort niet met een oude donkere stand te
    // starten: het scherm staat dan per definitie weer te tekenen.
    awake = true;
    darkAt = null;
  };
}
